import { useCallback, useMemo, useState, useEffect } from 'react';
import { useParams, useNavigate, useSearchParams } from 'react-router-dom';
import { differenceInCalendarDays } from 'date-fns';
import { v4 as uuidv4 } from 'uuid';
import { map, get, set, size, omit, find, isEmpty } from 'lodash';

import { Button, Input, Textarea, Icon, showToast, Card, FileUpload, Select, Tooltip } from '@repo/ui';
import type { SelectOption } from '@repo/ui';
import { dateUtil } from '@xtnd/web/shared/utils';
import { PageLayout } from '@xtnd/web/shared/layout';
import {
  useGetAttorneyResponse,
  useMatterInvoiceSummaryFilesQuery,
  usePatchAttorneyResponse,
  useSubmitAttorneyResponse,
  useUpdateAttorneyResponse,
  useUpdateDelegate,
  useUploadMatterInvoiceSummaryFiles,
} from '../queries';
import { useDelegateUsers } from '../queries/userQuery';
import { AttorneyResponsesContent } from '../components/common';
import { AttorneyResponseStatus, UserRole } from '@repo/types';
import {
  AttorneyResponse,
  AttorneyResponsePatchPayload,
  FileCategory,
  MatterInvoiceSummaryFile,
  Timekeeper,
} from '../types';
import { AttorneyResponseItem } from '../components/common/AttorneyResponseItem';
import { AR_ATTACHMENT_ACCEPT } from '../constants/attachments';
import { authApi } from '@xtnd/web/features/auth/api';
import { xtndAnimationStyles } from '@repo/tailwind-config';
import { FileAttachments } from '../components/common/FileAttachments';
import {
  AppealOptionSelector,
  AttorneyResponseSkeleton,
  EditingResponseView,
  InvoiceDetails,
  InvoiceNotFound,
  UnauthorizedAccess,
} from '../components/attorney-response';
import {
  buildAttorneyResponsePayload,
  DEFAULT_LINE_ITEM,
  ANIMATION_DELAY_200MS_STYLE,
  formatResponseTimestamp,
  isUnauthorizedErrorMessage,
} from '../utils/attorney-response';
import type { LineItem, LineItemErrors } from '../utils/attorney-response';

const ROLE_ACCESS_VIEW_RESPONSE = [
  UserRole.CLERK,
  UserRole.BILLING_ADMIN,
  UserRole.BOOKKEEPER,
  UserRole.ACCOUNTING_ADMIN,
  UserRole.SUPERVISOR,
  UserRole.ADMIN,
  UserRole.HR_ADMIN,
];

const ROLE_EDIT_RESPONSE = [
  UserRole.BILLING_ADMIN,
  UserRole.ACCOUNTING_ADMIN,
  UserRole.SUPERVISOR,
  UserRole.ADMIN,
  UserRole.HR_ADMIN,
];


export const ArAttorneyResponsePage = () => {
  const { invoiceId, appealId } = useParams<{ invoiceId: string; appealId: string }>();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const timekeeperIdFromQuery = searchParams.get('timekeeperId');

  const {
    data: invoice,
    isLoading,
    isFetching,
    error,
  } = useGetAttorneyResponse(invoiceId || '', appealId || '', {
    enabled: !!invoiceId && !!appealId,
  });

  const { mutateAsync: uploadFiles } = useUploadMatterInvoiceSummaryFiles(
    invoiceId || '',
    'AR Appeal',
    {
      isShowToast: false,
      category: FileCategory.APPEALS_SUPPORT,
    }
  );

  const { mutateAsync: submitAttorneyResponse } = useSubmitAttorneyResponse();
  const { mutateAsync: updateAttorneyResponse } = useUpdateAttorneyResponse();
  const { mutateAsync: patchAttorneyResponse, isPending: isPatchingAttorneyResponse } =
    usePatchAttorneyResponse();
  const { mutateAsync: updateDelegate, isPending: isUpdatingDelegate } = useUpdateDelegate();
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [files, setFiles] = useState<File[]>([]);
  const [fileErrors, setFileErrors] = useState<string>('');
  const [attachments, setAttachments] = useState<MatterInvoiceSummaryFile[]>([]);

  // Check if error is authorization error
  const isUnauthorizedError = useMemo(() => {
    return isUnauthorizedErrorMessage(error);
  }, [error]);

  const [selectedOption, setSelectedOption] = useState<'appeal' | 'no-appeal'>('appeal');
  const [lineItems, setLineItems] = useState<LineItem[]>([DEFAULT_LINE_ITEM]);
  const [errors, setErrors] = useState<Record<string, LineItemErrors>>({});
  const [isEditMode, setIsEditMode] = useState(false);
  const [editingTimekeeperId, setEditingTimekeeperId] = useState<string | null>(null);
  const { data: allUsers = [], isLoading: isLoadingUsers } = useDelegateUsers();
  const [delegateOverrides, setDelegateOverrides] = useState<Record<string, string | null>>({});

  const handleDelegateChange = useCallback(
    async (option: string | SelectOption | null, timekeeperId: string) => {
      if (!invoiceId || !timekeeperId) return;

      const email = typeof option === 'string' ? option : option ? option.value : null;
      setDelegateOverrides(prev => ({ ...prev, [timekeeperId]: email }));

      let delegateAttyCode: string | null = null;
      if (email) {
        const user = allUsers.find(u => u.email === email);
        delegateAttyCode = user?.attyCode ?? null;
      }

      try {
        await updateDelegate({
          invoiceId,
          appealId: appealId || '',
          timekeeperId,
          payload: {
            delegate: delegateAttyCode,
            delegateEmail: email,
          },
        });
      } catch {
        // Error toast is handled by the mutation hook
      }
    },
    [invoiceId, appealId, allUsers, updateDelegate]
  );

  const daysPastDue = useMemo(() => {
    if (!invoice?.invoiceDueAt) return 0;

    return Math.max(0, differenceInCalendarDays(new Date(), new Date(invoice.invoiceDueAt)));
  }, [invoice?.invoiceDueAt]);

  const totalAmount = invoice?.total || 0;
  const amountDue = invoice?.outstandingBalance || 0;

  const { data: existingFilesResponse } = useMatterInvoiceSummaryFilesQuery(invoiceId ?? '', {
    enabled: !!invoiceId,
  });

  // Check if admin is editing a response for a specific timekeeper
  useEffect(() => {
    if (invoice && timekeeperIdFromQuery) {
      const currentUser = authApi.getCurrentUser();
      const currentUserRole = get(currentUser, 'roles.0', '') as UserRole;
      const currentAttyCode = currentUser?.attyCode;

      const timekeepers = get(invoice, 'appeal.timekeepers', []);
      const attorneyResponses = get(invoice, 'appeal.attorneyResponses', []);
      const currentAttorneyResponse = attorneyResponses.find(
        (response: AttorneyResponse) => response.id === timekeeperIdFromQuery
      );

      // Find the specific timekeeper being edited and check if the current user is associated with it
      const editingTimekeeper = timekeepers.find(
        (timekeeper: Timekeeper) => timekeeper.id === timekeeperIdFromQuery
      );
      const isAssociatedWithEditingTimekeeper =
        editingTimekeeper?.id === currentAttyCode || editingTimekeeper?.delegate === currentAttyCode;

      const status = get(currentAttorneyResponse, 'status');
      const isEditable = ROLE_EDIT_RESPONSE.includes(currentUserRole);

      // Allow edit if: user is admin/edit role, OR user is the timekeeper/delegate and (status is approved or no response yet)
      if (
        (isAssociatedWithEditingTimekeeper && (status === AttorneyResponseStatus.EDIT_APPROVED || !currentAttorneyResponse)) ||
        isEditable
      ) {
        setIsEditMode(true);
        setEditingTimekeeperId(timekeeperIdFromQuery);
        setErrors({});
        setFileErrors('');

        // Load existing response for this timekeeper

        const existingResponse = find(
          attorneyResponses,
          (response: AttorneyResponse) => response.id === timekeeperIdFromQuery
        );

        if (existingResponse) {
          // Pre-fill form with existing response data
          setSelectedOption(existingResponse.appealAction === 'APPEAL' ? 'appeal' : 'no-appeal');
          const attachments = existingResponse.attachments || [];
          setAttachments(attachments as MatterInvoiceSummaryFile[]);

          // Convert existing line items to form format
          if (existingResponse.lineItems && existingResponse.lineItems.length > 0) {
            const formattedLineItems: LineItem[] = existingResponse.lineItems.map(item => ({
              id: uuidv4(),
              lineNumber: item.lineNumber || '',
              comment: item.comment || '',
            }));
            setLineItems(formattedLineItems);
          } else {
            setLineItems([DEFAULT_LINE_ITEM]);
          }

          // Note: Files cannot be pre-loaded, admin will need to re-upload if needed
          setFiles([]);
        } else {
          setSelectedOption('appeal');
          setFiles([]);
          setLineItems([DEFAULT_LINE_ITEM]);
        }
      } else {
        navigate(
          `/ar-collections/public/invoice/${invoiceId}/appeal/${appealId}/attorney-response`
        );
        setIsEditMode(false);
        setEditingTimekeeperId(null);
      }
    }
  }, [appealId, invoice, invoiceId, navigate, timekeeperIdFromQuery]);

  const validateLineItems = useCallback((): boolean => {
    const newErrors: Record<string, LineItemErrors> = {};
    let hasErrors = false;

    lineItems.forEach(item => {
      const itemErrors: LineItemErrors = {};

      if (!item.lineNumber || item.lineNumber.trim() === '') {
        itemErrors.lineNumber = 'Line number is required.';
        hasErrors = true;
      }

      if (!item.comment || item.comment.trim() === '') {
        itemErrors.comment = 'Comment is required.';
        hasErrors = true;
      }

      if (item.lineNumber && Number(item.lineNumber) < 1) {
        itemErrors.lineNumber = 'Line number must be greater than 0.';
        hasErrors = true;
      }

      if (Object.keys(itemErrors).length > 0) {
        newErrors[item.id] = itemErrors;
      }
    });

    setErrors(newErrors);

    return !hasErrors;
  }, [lineItems]);

  const handleSubmit = useCallback(async () => {
    if (!invoiceId || !appealId) {
      showToast.error('Invalid invoice or appeal ID');
      return;
    }

    let attachmentsPayload = [
      ...attachments.map(attachment => ({
        ...attachment,
        category: FileCategory.PAYMENT_SUPPORT,
      })),
    ] as MatterInvoiceSummaryFile[];

    try {
      let hasError = false;
      if (selectedOption === 'appeal') {
        // Validate line items for appeal option
        if (!validateLineItems()) {
          hasError = true;
        }
      }

      setIsSubmitting(true);

      if (size(files) > 0) {
        const uploadResult = await uploadFiles({
          files: files,
        });

        const { successFiles, errorFiles } = uploadResult;

        if (errorFiles && size(errorFiles) > 0) {
          showToast.error('Some files failed to upload. Please try again.');
          return;
        }

        if (successFiles && size(successFiles) > 0) {
          attachmentsPayload = [...attachmentsPayload, ...successFiles];
        }
      }

      if (hasError) {
        setIsSubmitting(false);
        return;
      }

      const payload = buildAttorneyResponsePayload(selectedOption, lineItems, attachmentsPayload);

      // If not in edit mode and not editing a specific timekeeper, submit the response
      if (!isEditMode && !editingTimekeeperId) {
        await submitAttorneyResponse({ invoiceId, appealId, payload });

        navigate(0);
        return;
      }

      if (!editingTimekeeperId) {
        showToast.error('Edit mode is unavailable because the Timekeeper ID was not found.');
        setIsSubmitting(false);
        return;
      }

      await updateAttorneyResponse({
        invoiceId,
        appealId,
        timekeeperId: editingTimekeeperId,
        payload,
      });

      setEditingTimekeeperId(null);
      setIsEditMode(false);
      navigate(`/ar-collections/public/invoice/${invoiceId}/appeal/${appealId}/attorney-response`);
      return;
    } finally {
      setIsSubmitting(false);
    }
  }, [
    invoiceId,
    appealId,
    attachments,
    selectedOption,
    files,
    lineItems,
    isEditMode,
    editingTimekeeperId,
    validateLineItems,
    uploadFiles,
    submitAttorneyResponse,
    navigate,
    updateAttorneyResponse,
  ]);

  const handleChangeLineItem = useCallback((id: string, field: keyof LineItem, value: any) => {
    setLineItems(prev =>
      map(prev, item => {
        if (item.id !== id) return item;

        // Handle normal fields
        const updatedItem = set({ ...item }, field, value);

        // Clear error for this field when user starts typing
        if (field === 'lineNumber' || field === 'comment') {
          setErrors(prev => {
            const itemErrors = prev[id];
            if (!itemErrors || !itemErrors[field]) {
              return prev; // No error to clear
            }

            const newErrors = { ...prev };
            const updatedItemErrors = { ...itemErrors };
            delete updatedItemErrors[field];

            if (Object.keys(updatedItemErrors).length === 0) {
              // Remove item from errors if no errors left
              delete newErrors[id];
            } else {
              newErrors[id] = updatedItemErrors;
            }

            return newErrors;
          });
        }

        return updatedItem;
      })
    );
  }, []);

  const handleAddLineItem = useCallback(() => {
    setLineItems(prev => [...prev, { ...DEFAULT_LINE_ITEM, id: uuidv4() }]);
  }, []);

  const handleRemoveLineItem = useCallback(
    (id: string) => {
      setLineItems(prev => prev.filter(item => item.id !== id));
      setErrors(prev => omit(prev, [id]));
      if (size(lineItems) === 1) {
        setLineItems([DEFAULT_LINE_ITEM]);
      }
    },
    [lineItems]
  );

  const handleFilesChange = useCallback((files: File[]) => {
    setFiles(files);
    if (files.length > 0) {
      setFileErrors('');
    }
  }, []);

  // Handler for Edit Response button click (for admin edit mode)
  const handleEditResponse = useCallback(
    ({
      invoiceId: invId,
      appealId: appId,
      timekeeperId,
    }: {
      invoiceId: string;
      appealId: string;
      timekeeperId: string;
    }) => {
      // Navigate to attorney response page with timekeeperId as query param
      navigate(
        `/ar-collections/public/invoice/${invId}/appeal/${appId}/attorney-response?timekeeperId=${timekeeperId}`
      );
    },
    [navigate]
  );

  // Handler for Back to List button (exit edit mode)
  const handleBackToList = useCallback(() => {
    if (!invoiceId || !appealId) return;
    // Navigate back to list view (remove timekeeperId query param)
    const backUrl = `/ar-collections/public/invoice/${invoiceId}/appeal/${appealId}/attorney-response`;
    navigate(backUrl);
    // Reset edit mode state
    setIsEditMode(false);
    setEditingTimekeeperId(null);
  }, [invoiceId, appealId, navigate]);

  const getDelegateEmailForTimekeeper = useCallback(
    (timekeeperId: string) => {
      if (timekeeperId in delegateOverrides) {
        return delegateOverrides[timekeeperId];
      }
      const timekeepers = get(invoice, 'appeal.timekeepers', []) as Timekeeper[];
      const timekeeper = timekeepers.find((tk: Timekeeper) => tk.id === timekeeperId);
      return timekeeper?.delegateEmail ?? null;
    },
    [invoice, delegateOverrides]
  );

  const getDelegateLabelForTimekeeper = useCallback(
    (timekeeperId: string) => {
      const email = getDelegateEmailForTimekeeper(timekeeperId);
      if (!email) return '';
      const user = allUsers.find(u => u.email === email);
      return user?.label || email;
    },
    [getDelegateEmailForTimekeeper, allUsers]
  );

  const renderDelegateSection = useCallback(
    (timekeeperId: string) => {
      const tkDelegateEmail = getDelegateEmailForTimekeeper(timekeeperId);
      const tkDelegateLabel = getDelegateLabelForTimekeeper(timekeeperId);

      return (
        <Card className="flex items-center gap-4 opacity-0 animate-fade-in-up" style={ANIMATION_DELAY_200MS_STYLE}>
          <Icon name="HiUser" size="sm" className="text-gray-500 dark:text-gray-400" />
          <h3 className="text-sm font-semibold text-xtnd-dark dark:text-xtnd-light whitespace-nowrap">
            Delegate
          </h3>
          <Tooltip content={tkDelegateLabel} position="bottom" disabled={!tkDelegateEmail}>
            <div className="w-full md:w-80">
              <Select
                options={allUsers}
                value={tkDelegateEmail}
                onChange={option => handleDelegateChange(option, timekeeperId)}
                placeholder="Choose and delegate to a user..."
                searchable
                clearable
                isLoading={isLoadingUsers || isUpdatingDelegate}
                disabled={isSubmitting || isUpdatingDelegate}
                size="md"
              />
            </div>
          </Tooltip>
        </Card>
      );
    },
    [allUsers, isLoadingUsers, isSubmitting, isUpdatingDelegate, handleDelegateChange, getDelegateEmailForTimekeeper, getDelegateLabelForTimekeeper]
  );

  const handleDeleteAttachment = useCallback((file: MatterInvoiceSummaryFile) => {
    setAttachments(prev => prev.filter(f => f.fileId !== file.fileId));
  }, []);

  const handleRequestRevision = useCallback(
    async ({ timekeeperId, status }: { timekeeperId: string; status: AttorneyResponseStatus }) => {
      if (!invoiceId || !appealId) {
        showToast.error('Invalid invoice or appeal ID');
        return;
      }

      const payload: AttorneyResponsePatchPayload = {
        status,
      };

      await patchAttorneyResponse({
        invoiceId,
        appealId,
        timekeeperId,
        payload,
      });
    },
    [invoiceId, appealId, patchAttorneyResponse]
  );

  const existingFiles = useMemo(
    () => existingFilesResponse?.data ?? [],
    [existingFilesResponse?.data]
  );

  const paymentSupportAttachments = useMemo(
    () => existingFiles.filter(file => file.category === FileCategory.PAYMENT_SUPPORT),
    [existingFiles]
  );

  const appealsSupportAttachments = useMemo(
    () => existingFiles.filter(file => file.category === FileCategory.APPEALS_SUPPORT),
    [existingFiles]
  );

  const renderHeaderAttorneyAction = (
    existingAttorneyResponse: AttorneyResponse,
    currentUserEmail: string
  ) => {
    const submittedAt = formatResponseTimestamp(
      existingAttorneyResponse?.updatedAt || existingAttorneyResponse?.createdAt
    );

    switch (existingAttorneyResponse.status) {
      case AttorneyResponseStatus.SUBMITTED:
        return (
          <>
            <div className="w-full flex justify-end">
              <Button
                loading={isPatchingAttorneyResponse}
                disabled={isPatchingAttorneyResponse}
                type="button"
                variant="primary"
                leftIcon={<Icon name="HiExclamationCircle" size="md" />}
                onClick={() =>
                  handleRequestRevision({
                    timekeeperId: existingAttorneyResponse.id as string,
                    status: AttorneyResponseStatus.EDIT_REQUESTED,
                  })
                }
                className="flex items-center px-4 py-2 bg-xtnd-blue text-white rounded-full shadow-sm shadow-xtnd-blue/20 hover:bg-xtnd-blue-700 hover:shadow-xtnd-blue/30 transition-all transform hover:-translate-y-0.5"
              >
                <span className="text-sm font-bold">Request Revision</span>
              </Button>
            </div>
            <div className="bg-blue-50 dark:bg-blue-900/30 border border-blue-200 dark:border-blue-800 rounded-lg p-4 flex items-start gap-3 shadow-sm animate-in slide-in-from-top duration-300">
              <div className="text-blue-900 dark:text-blue-300 mt-0.5">
                <Icon name="HiInformationCircle" size="md" />
              </div>
              <div>
                <h4 className="text-sm font-bold text-blue-900 dark:text-blue-200">
                  Response Already Submitted
                </h4>

                {existingAttorneyResponse.createdBy === currentUserEmail ? (
                  <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                    You have already submitted an appeal response for this request on{' '}
                    <strong>{submittedAt}</strong>. You can view the data, but further edits are not
                    allowed.
                  </p>
                ) : (
                  <p className="text-sm text-blue-700 dark:text-blue-300 mt-1">
                    This response was submitted by{' '}
                    <strong>
                      {existingAttorneyResponse.updatedByName ||
                        existingAttorneyResponse.createdByName}
                    </strong>{' '}
                    on <strong>{submittedAt}</strong>. You can view the data, but further edits are
                    not allowed.
                  </p>
                )}
              </div>
            </div>
          </>
        );
      case AttorneyResponseStatus.EDIT_REQUESTED:
        return (
          <div className="w-full bg-blue-50 border border-blue-200 rounded-lg p-4 flex items-start gap-3 shadow-sm animate-in slide-in-from-top duration-300">
            <div className="text-blue-700 mt-0.5">
              <Icon name="HiClock" size="md" />
            </div>
            <div>
              <h4 className="text-sm font-bold text-blue-900">Revision Requested</h4>

              <p className="text-sm text-blue-700 mt-1">
                A revision was requested on{' '}
                <strong>
                  {dateUtil.formatDate(
                    new Date(existingAttorneyResponse.revisionActionAt),
                    dateUtil.DateFormat.US_WITH_TIME
                  )}
                </strong>
                . This request is pending admin approval.
              </p>
            </div>
          </div>
        );
      case AttorneyResponseStatus.EDIT_APPROVED:
        return (
          <>
            <div className="w-full flex justify-end">
              <Button
                loading={isPatchingAttorneyResponse}
                disabled={isPatchingAttorneyResponse}
                type="button"
                variant="primary"
                leftIcon={<Icon name="HiPencil" size="md" />}
                onClick={() => {
                  handleEditResponse({
                    invoiceId: invoiceId || '',
                    appealId: appealId || '',
                    timekeeperId: existingAttorneyResponse.id as string,
                  });
                }}
                className="flex items-center gap-1 px-4 py-2 bg-xtnd-blue text-white rounded-full shadow-sm shadow-xtnd-blue/20 hover:bg-xtnd-blue-700 hover:shadow-xtnd-blue/30 transition-all transform hover:-translate-y-0.5"
              >
                <span className="text-sm font-medium">Edit Response</span>
              </Button>
            </div>
            <div className="bg-green-50 border border-green-200 rounded-lg p-4 flex items-start gap-3 shadow-sm animate-in slide-in-from-top duration-300">
              <div className="text-green-700 mt-0.5">
                <Icon name="HiCheckCircle" size="md" />
              </div>
              <div>
                <h4 className="text-sm font-bold text-green-900">Revision Request Approved</h4>

                <p className="text-sm text-green-700 mt-1">
                  Your revision request was approved on{' '}
                  <strong>
                    {dateUtil.formatDate(
                      new Date(existingAttorneyResponse.revisionActionAt),
                      dateUtil.DateFormat.US_WITH_TIME
                    )}
                  </strong>
                  . You may now proceed with updating the response.
                </p>
              </div>
            </div>
          </>
        );
      default:
        return null;
    }
  };

  const renderAction = useCallback(() => {
    if (selectedOption === 'no-appeal') {
      return (
        <div className="opacity-0 animate-fade-in-up" style={ANIMATION_DELAY_200MS_STYLE}>
          <div id="no-appeal-details-section" className="animate-in fade-in duration-200">
            <div className="p-4 bg-amber-50 dark:bg-amber-900/20 border border-amber-200 dark:border-amber-800 rounded-lg mb-8">
              <div className="flex">
                <Icon
                  name="HiExclamationCircle"
                  size="md"
                  className="text-amber-500 dark:text-amber-400 mr-3 flex-shrink-0 mt-0.5"
                />
                <p className="text-sm text-amber-700 dark:text-amber-300">
                  You have selected <strong>No Appeal</strong>. After submission, the attorney's
                  responsibility is complete, and the Appeals team can proceed with write-off
                  evaluation.
                </p>
              </div>
            </div>
            <div className="mt-4 flex justify-end">
              <Button
                variant="primary"
                type="button"
                onClick={handleSubmit}
                disabled={isSubmitting}
                loading={isSubmitting}
                className="px-8"
              >
                Submit
              </Button>
            </div>
          </div>
        </div>
      );
    }
    return (
      <div
        className="opacity-0 animate-fade-in-up duration-200"
        style={ANIMATION_DELAY_200MS_STYLE}
      >
        <div id="appeal-details-section" className="space-y-6">
          <Card>
            <label className="flex flex-col text-sm font-semibold text-xtnd-dark dark:text-xtnd-light mb-1">
              <p>
                Attachments
              </p>
            </label>
            <FileUpload
              files={files}
              isMultiple={true}
              accept={AR_ATTACHMENT_ACCEPT}
              maxSize={30 * 1024 * 1024}
              disabled={isLoading || isSubmitting}
              onChange={handleFilesChange}
              isDisplayUploadButton={false}
              maxFileNumber={20}
              error={fileErrors}
              setError={(error: string) => setFileErrors(error)}
              className="max-w-full"
            />
            <div className="mt-4">
              <div className="space-y-4">
                <div className="space-y-2">
                  <p className="text-sm font-semibold text-xtnd-dark dark:text-xtnd-light">Appeals Support</p>
                </div>
                {appealsSupportAttachments.length > 0 ? (
                  <FileAttachments
                    files={appealsSupportAttachments}
                    showCheckbox={false}
                    columns={1}
                  />
                ) : (
                  <div className="text-sm text-xtnd-dark dark:text-xtnd-light border border-gray-200 dark:border-gray-700 rounded-lg p-4 justify-center">
                    <div className="flex flex-col items-center justify-center gap-2">
                      <Icon
                        name="HiDocumentReport"
                        size="sm"
                        className="text-gray-500 dark:text-xtnd-light"
                      />
                      <p className="text-sm text-gray-500 dark:text-xtnd-light">No documents available</p>
                    </div>
                  </div>
                )}
                <div className="space-y-2">
                  <p className="text-sm font-semibold text-xtnd-dark dark:text-xtnd-light">Payment Support</p>
                </div>
                {paymentSupportAttachments.length > 0 ? (
                  <FileAttachments
                    files={paymentSupportAttachments}
                    showCheckbox={false}
                    columns={1}
                  />
                ) : (
                  <div className="text-sm text-xtnd-dark dark:text-xtnd-light border border-gray-200 dark:border-gray-700 rounded-lg p-4 justify-center">
                    <div className="flex flex-col items-center justify-center gap-2">
                      <Icon
                        name="HiDocumentReport"
                        size="sm"
                        className="text-gray-500 dark:text-xtnd-light"
                      />
                      <p className="text-sm text-gray-500 dark:text-xtnd-light">No documents available</p>
                    </div>
                  </div>
                )}
              </div>
            </div>
          </Card>
          <Card className="space-y-4">
            {/* Header */}
            <div className="flex items-center justify-between border-b border-gray-300 dark:border-xtnd-dark-600 pb-2">
              <h2 className="text-sm font-bold text-xtnd-dark dark:text-xtnd-light uppercase tracking-wider">
                Line Item 
              </h2>
              <div className="text-xs text-gray-500 dark:text-gray-400 italic">
                <span className="font-bold text-red-500">*</span> Line Number and Comment are
                required
              </div>
            </div>

            {/* Table header (desktop) */}
            <div className="hidden md:grid grid-cols-[1fr_10fr_0.5fr] gap-4 px-2 text-xs font-semibold text-gray-500 dark:text-gray-400 uppercase">
              <div>Line # <span className="text-red-500">*</span></div>
              <div>Comment <span className="text-red-500">*</span></div>
              <div className="text-center">Delete</div>
            </div>

            {/* Line items */}
            <div id="line-items-container" className="space-y-3">
              {lineItems.map(lineItem => (
                <div
                  key={lineItem.id}
                  className="line-item-row md:grid grid-cols-[1fr_10fr_0.5fr] gap-3 p-3 border dark:border-xtnd-dark-600 rounded-lg bg-white dark:bg-xtnd-dark-800 shadow-sm relative group"
                >
                  <div>
                    <label className="block md:hidden text-xs font-bold text-xtnd-dark dark:text-xtnd-light mb-1">
                      Line Number
                    </label>
                    <Input
                      type="number"
                      placeholder="e.g. 10"
                      size="sm"
                      min={1}
                      disabled={isSubmitting}
                      value={lineItem.lineNumber}
                      onChange={e =>
                        handleChangeLineItem(lineItem.id, 'lineNumber', e.target.value)
                      }
                      fullWidth
                      error={errors[lineItem.id]?.lineNumber}
                      required
                      className="focus:ring-xtnd-blue focus:border-xtnd-blue border-gray-300 dark:border-xtnd-dark-600"
                    />
                  </div>

                  {/* Comment */}
                  <div>
                    <label className="block md:hidden text-xs font-bold text-xtnd-dark dark:text-xtnd-light mb-1">
                      Comment
                    </label>
                    <Textarea
                      rows={1}
                      placeholder="Enter appeal reason..."
                      fullWidth
                      autoResize
                      disabled={isSubmitting}
                      value={lineItem.comment}
                      onChange={e => handleChangeLineItem(lineItem.id, 'comment', e.target.value)}
                      minRows={3}
                      error={errors[lineItem.id]?.comment}
                      required
                      className="resize-none h-auto overflow-y-auto"
                    />
                  </div>

                  {/* Delete */}
                  <div className="ml-4">
                    <div className="h-[34px]  flex items-center ">
                      <button
                        type="button"
                        onClick={() => handleRemoveLineItem(lineItem.id)}
                        disabled={isSubmitting}
                        className="text-gray-400 dark:text-gray-500 hover:text-red-500 dark:hover:text-red-400 transition-colors hover:bg-transparent focus:ring-0 focus:ring-offset-0"
                      >
                        <Icon name="HiTrash" size="md" />
                      </button>
                    </div>
                  </div>
                </div>
              ))}
            </div>
            {/* Add new line */}
            <button
              type="button"
              onClick={() => handleAddLineItem()}
              disabled={isSubmitting}
              className="text-xtnd-dark-500 dark:text-xtnd-blue border-none hover:bg-transparent hover:text-xtnd-blue focus:ring-0 focus:ring-offset-0 flex items-center gap-2"
            >
              <Icon name="HiPlus" size="sm" /> Add new appeal line
            </button>
          </Card>
          <div className="mt-4 flex justify-end">
            <Button
              variant="primary"
              type="button"
              onClick={handleSubmit}
              loading={isSubmitting}
              disabled={Object.keys(errors).length > 0 || isSubmitting}
              className="px-8"
            >
              Submit
            </Button>
          </div>
        </div>
      </div>
    );
}, [selectedOption, files, isLoading, isSubmitting, handleFilesChange, fileErrors, lineItems, handleSubmit, errors, handleChangeLineItem, handleRemoveLineItem, handleAddLineItem, paymentSupportAttachments, appealsSupportAttachments]);

  const renderPageContent = () => {
    if (isLoading || isFetching) {
      return <AttorneyResponseSkeleton />;
    }

    if (isUnauthorizedError) {
      return <UnauthorizedAccess />;
    }

    if (invoice) {
      const currentUser = authApi.getCurrentUser();
      const currentAttyCode = currentUser?.attyCode;
      const currentUserEmail = currentUser?.email;
      const attorneyResponses = get(invoice, 'appeal.attorneyResponses', []);
      const timekeepers = get(invoice, 'appeal.timekeepers', []);
      const associatedTimekeepers = timekeepers.filter(
        (timekeeper: Timekeeper) =>
          timekeeper.id === currentAttyCode || timekeeper.delegate === currentAttyCode
      );
      const currentTimekeeper = associatedTimekeepers[0] || null;
      const hasMultipleAssociations = associatedTimekeepers.length > 1;
      const currentUserRole = get(currentUser, 'roles.0') as UserRole;
      const hasViewRole = ROLE_ACCESS_VIEW_RESPONSE.includes(currentUserRole);
      const canDelegate = ROLE_EDIT_RESPONSE.includes(currentUserRole) || !!currentTimekeeper;

      const getDelegateSection = (timekeeperId: string) => {
        if (canDelegate || timekeeperId === currentAttyCode) {
          return renderDelegateSection(timekeeperId);
        }
        return null;
      };

      // ADMIN AND SOME OTHER ROLES CAN VIEW THE LIST OF ATTORNEY RESPONSES.
      // Non-VIEW_ROLE users with multiple associations (timekeeper + delegate) also enter this view.
      if (!currentTimekeeper || hasViewRole || hasMultipleAssociations) {
        if (!hasViewRole && !currentTimekeeper) {
          return <UnauthorizedAccess />;
        }

        // GET APPEAL ID FROM INVOICE OR URL PARAMS
        const invoiceAppealId = get(invoice, 'appeal.appealId') || appealId || '';

        // FOR ADMIN TO EDIT A RESPONSE.
        if (isEditMode && editingTimekeeperId) {
          const existingResponse = find(
            attorneyResponses,
            (response: AttorneyResponse) => response.id === editingTimekeeperId
          );

          const editingTimekeeper = find(
            timekeepers,
            (tk: Timekeeper) => tk.id === editingTimekeeperId
          );

          // FOR ADMIN TO EDIT A RESPONSE WHEN THE ATTORNEY HAS NOT SUBMITTED IT YET.
          return (
            <EditingResponseView
              editingTimekeeper={editingTimekeeper}
              existingResponse={existingResponse ?? null}
              selectedOption={selectedOption}
              onBackToList={handleBackToList}
              onOptionChange={val => setSelectedOption(val)}
              invoice={invoice}
              totalAmount={totalAmount}
              amountDue={amountDue}
              daysPastDue={daysPastDue}
              renderAction={renderAction}
              delegateSection={getDelegateSection(editingTimekeeperId || '')}
            />
          );
        }

        // FOR ADMIN AND SOME OTHER ROLES CAN VIEW
        const userCanEdit = ROLE_EDIT_RESPONSE.includes(currentUserRole);

        // VIEW_ROLE users see all responses; non-VIEW_ROLE users only see their own
        const displayTimekeepers = hasViewRole ? timekeepers : associatedTimekeepers;
        const displayResponses = hasViewRole
          ? attorneyResponses
          : attorneyResponses.filter((r: AttorneyResponse) =>
              associatedTimekeepers.some((tk: Timekeeper) => tk.id === r.id)
            );

        return (
          <div className="flex-1">
            <AttorneyResponsesContent
              timekeepers={displayTimekeepers}
              attorneyResponses={displayResponses}
              isLoading={false}
              allowViewAll={hasViewRole}
              autoSelectFirst={true}
              className="flex-1 min-h-0 grid-cols-[1fr_3fr]"
              canEditMode={userCanEdit}
              currentAttyCode={currentAttyCode}
              invoiceId={invoiceId || ''}
              appealId={invoiceAppealId}
              onEditResponse={handleEditResponse}
              renderDelegateSection={canDelegate ? getDelegateSection : undefined}
            />
          </div>
        );
      }

      // THE ATTORNEY HAS ALREADY RESPONDED
      const existingAttorneyResponse = attorneyResponses.find(
        (response: AttorneyResponse) => response.id === currentTimekeeper?.id
      ) as AttorneyResponse | undefined;

      if (existingAttorneyResponse && !isEditMode) {
        const lineItems = existingAttorneyResponse?.lineItems || [];

        const isOriginalTimekeeper = currentTimekeeper?.id === currentAttyCode;

        return (
          <div className="flex flex-col gap-6 text-sm text-xtnd-dark dark:text-xtnd-light">
            {renderHeaderAttorneyAction(existingAttorneyResponse, currentUserEmail || '')}
            {isOriginalTimekeeper && renderDelegateSection(currentAttyCode || '')}
            <InvoiceDetails
              invoice={invoice}
              totalAmount={totalAmount}
              amountDue={amountDue}
              daysPastDue={daysPastDue}
              className=" p-4 rounded-lg transition-shadow bg-xtnd-white dark:bg-xtnd-dark-800 border border-xtnd-light dark:border-xtnd-dark-700 shadow-xtnd-md"
            />
            <AttorneyResponseItem
              appealAction={existingAttorneyResponse.appealAction}
              attachments={existingAttorneyResponse.attachments}
              lineItems={lineItems}
              timekeeperName={currentTimekeeper?.name || ''}
              submittedBy={
                existingAttorneyResponse.updatedByName || existingAttorneyResponse.createdBy
              }
              submittedByRole={existingAttorneyResponse.submittedByRole}
              submittedAt={
                existingAttorneyResponse?.updatedAt ||
                existingAttorneyResponse?.createdAt ||
                new Date().toISOString()
              }
              delegateEmail={existingAttorneyResponse.delegateEmail}
            />
          </div>
        );
      }

      // THE ATTORNEY HAS NOT RESPONDED YET
      return (
        <div className="flex flex-col gap-6 text-sm text-xtnd-dark dark:text-xtnd-light">
          {/* Delegation */}
          {currentTimekeeper?.id === currentAttyCode && renderDelegateSection(currentAttyCode || '')}

          {/* Appeal Option Selector */}
          <AppealOptionSelector
            value={selectedOption}
            onValueChange={val => setSelectedOption(val)}
          />

          {/* Invoice Details */}
          <InvoiceDetails
            invoice={invoice}
            totalAmount={totalAmount}
            amountDue={amountDue}
            daysPastDue={daysPastDue}
          />

          {/* End Divider */}
          {/* ACTION */}
          <div>{renderAction()}</div>
        </div>
      );
    }

    return <InvoiceNotFound />;
  };

  const pageTitle = invoice?.invoiceNumber
    ? `Attorney Response - Invoice #${invoice.invoiceNumber}`
    : 'Attorney Response';

  return (
    <PageLayout
      title={pageTitle}
      description="Submit your attorney response for this appeal request"
    >
      <style>{xtndAnimationStyles}</style>
      <div className="min-h-[60vh] flex flex-col">
        <Card className="flex flex-col flex-1 min-h-0">{renderPageContent()}</Card>
      </div>
    </PageLayout>
  );
};
